import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { MessageService } from 'primeng/api';
import { map, take } from 'rxjs/operators';
import { AppUser } from '../users/user.model';
import { AuthenticationService } from '../services/authentication.service';
import { AuthorizationService } from '../services/authorization.service';
import { CrudAction } from '../master-data/permissions/permission.model';

/**
 * A factory function for creating a permission-based route guard.
 * This guard checks if the current user has at least one of the specified permissions
 * for a given resource.
 *
 * @param resourceName The name of the resource to check permissions for (e.g., 'Settlement').
 * @param requiredActions The action or actions the user must have permission for (e.g., 'create', or ['create', 'update']).
 * @param accessDeniedDetail The message to show to the user if access is denied.
 * @returns A `CanActivateFn` that can be used in a route definition.
 */
export function permissionGuard(
  resourceName: string,
  requiredActions: CrudAction | CrudAction[],
  accessDeniedDetail: string
): CanActivateFn {
  return () => {
    const authService = inject(AuthenticationService);
    const authorizationService = inject(AuthorizationService);
    const messageService = inject(MessageService);

    const actions = Array.isArray(requiredActions)
      ? requiredActions
      : [requiredActions];

    return authService.currentUser$.pipe(
      take(1),
      map((user: AppUser | null) => {
        if (authorizationService.hasPermission(user, resourceName, actions)) {
          return true;
        }

        messageService.add({
          severity: 'error',
          summary: 'Access Denied',
          detail: accessDeniedDetail,
          life: 5000
        });
        return false;
      })
    );
  };
}
